import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Gift, Users, Star, ArrowRight, MessageCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CookiesBanner from "../components/CookiesBanner";

const WHATSAPP_PHONE = "34608916357";

const fadeUp = {
	hidden: { opacity: 0, y: 30 },
	visible: (i = 0) => ({
		opacity: 1,
		y: 0,
		transition: { duration: 0.5, delay: i * 0.15 },
	}),
};

export default function Referrals() {
	const { t } = useTranslation();

	const steps = t("referrals.steps", { returnObjects: true });
	const benefits = t("referrals.benefits.items", { returnObjects: true });

	const whatsappMsg = encodeURIComponent(
		t("referrals.cta.whatsappMessage")
	);
	const whatsappHref = `https://wa.me/${WHATSAPP_PHONE}?text=${whatsappMsg}`;

	return (
		<div className="overflow-x-hidden bg-white">
			<Helmet>
				<title>{t("referrals.seo.title")}</title>
				<meta
					name="description"
					content={t("referrals.seo.description")}
				/>
				<link rel="canonical" href="https://applaby.com/referidos" />
				<meta
					property="og:title"
					content={t("referrals.seo.ogTitle")}
				/>
				<meta
					property="og:description"
					content={t("referrals.seo.ogDescription")}
				/>
				<meta property="og:url" content="https://applaby.com/referidos" />
				<meta property="og:type" content="website" />
			</Helmet>

			<Header />

			{/* Hero */}
			<section className="pt-32 pb-16 sm:pt-40 sm:pb-24 px-6 text-center bg-gradient-to-b from-green-50/60 to-white">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					className="max-w-3xl mx-auto"
				>
					<span className="inline-block mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-full bg-primary/10 text-primary">
						{t("referrals.hero.badge")}
					</span>
					<h1 className="text-3xl sm:text-5xl font-bold text-gray-900 leading-tight">
						{t("referrals.hero.title")}{" "}
						<span className="text-primary">
							{t("referrals.hero.titleHighlight")}
						</span>
					</h1>
					<p className="mt-5 text-lg text-gray-600 max-w-xl mx-auto">
						{t("referrals.hero.subtitle")}
					</p>
					<a
						href="#como-funciona"
						className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition"
					>
						{t("referrals.hero.cta")}
						<ArrowRight className="w-4 h-4" />
					</a>
				</motion.div>
			</section>

			{/* How it works */}
			<section
				id="como-funciona"
				className="py-16 sm:py-24 px-6 bg-white"
			>
				<div className="max-w-5xl mx-auto text-center">
					<motion.div
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						variants={fadeUp}
					>
						<span className="inline-block mb-3 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-full bg-primary/10 text-primary">
							{t("referrals.howItWorks.badge")}
						</span>
						<h2 className="text-2xl sm:text-4xl font-bold text-gray-900">
							{t("referrals.howItWorks.title")}
						</h2>
						<p className="mt-3 text-gray-500 max-w-lg mx-auto">
							{t("referrals.howItWorks.subtitle")}
						</p>
					</motion.div>

					<div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
						{Array.isArray(steps) &&
							steps.map((step, i) => {
								const icons = [Users, Gift, Star];
								const Icon = icons[i] || Gift;
								return (
									<motion.div
										key={i}
										custom={i}
										initial="hidden"
										whileInView="visible"
										viewport={{ once: true }}
										variants={fadeUp}
										className="relative p-6 rounded-2xl border border-gray-100 bg-gray-50/50 text-center"
									>
										<div className="mx-auto mb-4 w-14 h-14 flex items-center justify-center rounded-full bg-primary/10">
											<Icon className="w-6 h-6 text-primary" />
										</div>
										<span className="absolute top-4 right-4 text-xs font-bold text-primary/40">
											{String(i + 1).padStart(2, "0")}
										</span>
										<h3 className="text-lg font-semibold text-gray-900">
											{step.title}
										</h3>
										<p className="mt-2 text-sm text-gray-500">
											{step.description}
										</p>
									</motion.div>
								);
							})}
					</div>
				</div>
			</section>

			{/* Benefits */}
			<section className="py-16 sm:py-24 px-6 bg-gradient-to-b from-white to-green-50/40">
				<div className="max-w-5xl mx-auto">
					<motion.div
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						variants={fadeUp}
						className="text-center"
					>
						<span className="inline-block mb-3 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase rounded-full bg-primary/10 text-primary">
							{t("referrals.benefits.badge")}
						</span>
						<h2 className="text-2xl sm:text-4xl font-bold text-gray-900">
							{t("referrals.benefits.title")}
						</h2>
					</motion.div>

					<div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
						{Array.isArray(benefits) &&
							benefits.map((item, i) => (
								<motion.div
									key={i}
									custom={i}
									initial="hidden"
									whileInView="visible"
									viewport={{ once: true }}
									variants={fadeUp}
									className="flex items-start gap-4 p-5 rounded-xl bg-white border border-gray-100 shadow-sm"
								>
									<div className="mt-0.5 min-w-[36px] h-9 flex items-center justify-center rounded-lg bg-primary/10">
										<Star className="w-4 h-4 text-primary" />
									</div>
									<div>
										<h4 className="font-semibold text-gray-900">
											{item.title}
										</h4>
										<p className="mt-1 text-sm text-gray-500">
											{item.text}
										</p>
									</div>
								</motion.div>
							))}
					</div>
				</div>
			</section>

			{/* Reward highlight */}
			<section className="py-16 sm:py-20 px-6 bg-white">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					className="max-w-3xl mx-auto text-center p-8 sm:p-12 rounded-3xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/10"
				>
					<div className="mx-auto mb-5 w-16 h-16 flex items-center justify-center rounded-full bg-primary/15">
						<Gift className="w-8 h-8 text-primary" />
					</div>
					<h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
						{t("referrals.reward.title")}
					</h2>
					<p className="mt-3 text-gray-600 max-w-md mx-auto">
						{t("referrals.reward.description")}
					</p>
					<p className="mt-4 text-sm text-gray-400">
						{t("referrals.reward.note")}
					</p>
				</motion.div>
			</section>

			{/* CTA */}
			<section className="py-16 sm:py-24 px-6 bg-gray-900 text-white text-center">
				<motion.div
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
					variants={fadeUp}
					className="max-w-2xl mx-auto"
				>
					<h2 className="text-2xl sm:text-4xl font-bold">
						{t("referrals.cta.title")}
					</h2>
					<p className="mt-4 text-gray-300 text-lg">
						{t("referrals.cta.subtitle")}
					</p>
					<div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
						<a
							href={whatsappHref}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#25D366] text-white font-semibold shadow-md hover:shadow-lg hover:scale-[1.02] transition"
						>
							<MessageCircle className="w-5 h-5" />
							{t("referrals.cta.whatsapp")}
						</a>
						<a
							href="mailto:info@applaby.com"
							className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-white font-semibold hover:bg-white/10 transition"
						>
							{t("referrals.cta.email")}
						</a>
					</div>
				</motion.div>
			</section>

			<Footer />
			<CookiesBanner />
		</div>
	);
}
